import { useApolloClient } from '@apollo/client';
import {
  BooleanDotRenderer,
  type Column,
  createDateRangeFilterValidators,
  DateRenderer,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  NavigationExplorer,
  sortToPostGraphileOrderBy,
  transformRange,
} from '@axinom/mosaic-ui';
import React from 'react';
import {
  type EnvironmentFilter,
  EnvironmentsOrderBy,
  EnvironmentsWithDataDocument,
  type EnvironmentsWithDataQuery,
  type EnvironmentsWithDataQueryVariables,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import classes from './Environments.module.scss';

type EnvironmentData = NonNullable<
  EnvironmentsWithDataQuery['filtered']
>['nodes'][number];

export const StatusRenderer = (val: unknown): JSX.Element => (
  <div className={classes.status_container}>
    {BooleanDotRenderer(val)}
    <div className={classes.status}>{val ? 'Enabled' : 'Disabled'}</div>
  </div>
);

export const EnvironmentTypeRenderer = (value: unknown): JSX.Element => (
  <span>{value ? 'Development' : 'Production'}</span>
);

const explorerColumns: Column<EnvironmentData>[] = [
  { label: 'Name', propertyName: 'name' },
  {
    label: 'Status',
    propertyName: 'enabled',
    render: StatusRenderer,
    size: '150px',
  },
  {
    label: 'Environment Type',
    propertyName: 'isDevelopment',
    render: EnvironmentTypeRenderer,
    size: '170px',
  },
  { label: 'Created At', propertyName: 'createdAt', render: DateRenderer },
  { label: 'Created By', propertyName: 'createdBy' },
  {
    label: 'Last Modified At',
    propertyName: 'updatedAt',
    render: DateRenderer,
  },
  { label: 'Last Modified By', propertyName: 'updatedBy' },
];

export const Environments: React.FC = () => {
  const apolloClient = useApolloClient();

  const dataProvider: ExplorerDataProvider<EnvironmentData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const result = await apolloClient.query<
        EnvironmentsWithDataQuery,
        EnvironmentsWithDataQueryVariables
      >({
        query: EnvironmentsWithDataDocument,
        variables: {
          filter: filterToPostGraphileFilter<EnvironmentFilter>(filters, {
            name: 'includesInsensitive',
            enabled: 'in',
            isDevelopment: 'in',
            createdBy: 'includes',
            updatedBy: 'includes',
            createdAt: transformRange,
            updatedAt: transformRange,
          }),
          orderBy: sortToPostGraphileOrderBy(sorting, EnvironmentsOrderBy),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      const data = result.data.filtered?.nodes ?? [];

      return {
        data,
        totalCount: result.data.nonFiltered?.totalCount ?? 0,
        filteredCount: result.data.filtered?.totalCount ?? 0,
        hasMoreData: result.data.filtered?.pageInfo.hasNextPage ?? false,
        pagingInformation: result.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<EnvironmentData>();

  return (
    <div>
      <NavigationExplorer<EnvironmentData>
        title="Environments"
        stationKey="ServicePackagesExplorer"
        columns={explorerColumns}
        dataProvider={dataProvider}
        calculateNavigateUrl={(item) => `/env/${item.id}`}
        onCreateAction={`/env/create/`}
        defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
        filterOptions={[
          {
            label: 'Name',
            property: 'name',
            type: FilterTypes.FreeText,
          },
          {
            label: 'Status',
            property: 'enabled',
            type: FilterTypes.Options,
            options: [
              {
                label: 'Enabled',
                value: true,
              },
              {
                label: 'Disabled',
                value: false,
              },
            ],
          },
          {
            label: 'Environment Type',
            property: 'isDevelopment',
            type: FilterTypes.Options,
            options: [
              {
                label: 'Development',
                value: true,
              },
              {
                label: 'Production',
                value: false,
              },
            ],
          },
          {
            label: 'Creation Period (From)',
            property: 'createdAt',
            type: FilterTypes.Date,
            onValidate: createFromDateFilterValidator('createdAt'),
          },
          {
            label: 'Creation Period (To)',
            property: 'createdAt',
            type: FilterTypes.Date,
            onValidate: createToDateFilterValidator('createdAt'),
          },
          {
            label: 'Created By',
            property: 'createdBy',
            type: FilterTypes.FreeText,
          },
          {
            label: 'Modification Period (From)',
            property: 'updatedAt',
            type: FilterTypes.Date,
            onValidate: createFromDateFilterValidator('updatedAt'),
          },
          {
            label: 'Modification Period (To)',
            property: 'updatedAt',
            type: FilterTypes.Date,
            onValidate: createToDateFilterValidator('updatedAt'),
          },
          {
            label: 'Last Modified By',
            property: 'updatedBy',
            type: FilterTypes.FreeText,
          },
        ]}
      />
    </div>
  );
};

export const EnvironmentsCrumb: BreadcrumbResolver = () => 'Environments';
